import type { ResponseStatus } from '$src/types/forms';
import type { ParticipantType } from '$src/types/participants';

export class ParticipantService {
  private fetchHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-CSRF-Token': window.csrfTokenValue,
  };

  handleError(errorMessage: string) {
    alert(errorMessage);
  }

  async createParticipant(participantData: ParticipantType) {
    try {
      const response = await fetch('/api/users/participants/save/', {
        method: 'POST',
        headers: this.fetchHeaders,
        body: JSON.stringify(this.convertToFormData(participantData)),
      });

      if (!response.ok) {
        throw new Error('Failed to create participant');
      }

      const responseData = (await response.json()) as {
        data: {
          id: number;
        };
        status: ResponseStatus;
      };
      const createdParticipant = responseData;

      if (createdParticipant.status !== 'success') {
        throw new Error('Failed to create participant');
      }

      participantData.id = createdParticipant.data.id;

      return participantData;
    } catch {
      this.handleError(
        'Sorry, het is niet gelukt om een nieuwe deelnemer aan te maken. Probeer het opnieuw.'
      );

      return null;
    }
  }

  async updateParticipant(participantData: ParticipantType) {
    try {
      const response = await fetch('/api/users/participants/save/', {
        method: 'POST',
        headers: this.fetchHeaders,
        body: JSON.stringify(this.convertToFormData(participantData)),
      });

      if (!response.ok) {
        throw new Error('Failed to update participant');
      }

      const responseData = (await response.json()) as {
        status: ResponseStatus;
      };
      const updatedParticipant = responseData;

      return updatedParticipant;
    } catch {
      this.handleError(
        'Sorry, het is niet gelukt om de deelnemer te updaten. Probeer het opnieuw.'
      );

      return null;
    }
  }

  async deleteParticipant(participantId: number) {
    try {
      const response = await fetch('/api/users/participants/delete/', {
        method: 'POST',
        headers: this.fetchHeaders,
        body: JSON.stringify({ id: participantId }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete participant');
      }

      const responseData = (await response.json()) as {
        status: ResponseStatus;
      };
      const deletedParticipant = responseData;

      return deletedParticipant;
    } catch {
      this.handleError(
        'Sorry, het is niet gelukt om de deelnemer te verwijderen. Probeer het opnieuw.'
      );

      return null;
    }
  }

  private convertToFormData(participant: ParticipantType) {
    const formData = new FormData();

    formData.append('id', participant.id?.toString() ?? '');
    formData.append('firstName', participant.firstName);
    formData.append('lastName', participant.lastName);
    formData.append('gender', participant.gender);
    formData.append('dateOfBirth', participant.dateOfBirth);
    formData.append('city', participant.city);

    return Object.fromEntries(formData.entries());
  }

  renderGender(gender: string): string {
    const genderOptions = window.genderOptions;

    return genderOptions[gender] || 'Onbekend';
  }

  displayDateOfBirth(date: string) {
    const birthDate = new Date(date);
    const today = new Date();
    const month = today.getMonth() - birthDate.getMonth();

    let age = today.getFullYear() - birthDate.getFullYear();

    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    const dateOfBirth = birthDate.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Europe/Amsterdam',
    });

    return `${dateOfBirth} (${age.toString()} jaar)`;
  }
}
